import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Space,
  Typography,
  Form,
  Input,
  Select,
  DatePicker,
  Table,
  InputNumber,
  Divider,
  Tag,
  Statistic,
  Modal,
  List,
  Avatar,
  Tooltip,
  Alert,
  message,
  Drawer,
  Badge,
  Tabs,
  Progress,
  Upload,
  Popconfirm,
} from 'antd';
import {
  SaveOutlined,
  SendOutlined,
  EyeOutlined,
  PrinterOutlined,
  DownloadOutlined,
  CopyOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  SettingOutlined,
  UserOutlined,
  CalendarOutlined,
  DollarOutlined,
  FileTextOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  SearchOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import apiService from '../../../services/apiService';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface Customer {
  id: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  company?: string;
  companyId?: string;
}

interface Product {
  id: string;
  name: string;
  description?: string;
  unitPrice: number;
  unit: string;
  category?: string;
  sku?: string;
  alternateSkuList?: string[]; // 備用 SKU
}

interface QuotationItem {
  id: string;
  productId: string;
  product?: Product;
  description: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  discountType: 'percentage' | 'fixed';
  taxRate: number;
  total: number;
}

interface Template {
  id: string;
  name: string;
  type: 'QUOTATION';
  htmlTemplate: string;
  cssStyles?: string;
  isDefault: boolean;
}

interface Quotation {
  id?: string;
  quoteNo?: string;
  customerId?: string;
  customer?: Customer;
  quoteDate: Date;
  validUntil: Date;
  items: QuotationItem[];
  subtotal: number;
  totalDiscount: number;
  totalTax: number;
  grandTotal: number;
  notes: string;
  terms: string;
  status: 'DRAFT' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'EXPIRED';
  templateId: string;
  createdBy?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

interface QuotationBuilderProps {
  quotationId?: string;
  onSave?: (quotation: Quotation) => void;
  onSend?: (quotation: Quotation) => void;
  onClose?: () => void;
}

// Customers will be loaded from backend

const mockProducts: Product[] = [
  {
    id: '1',
    name: 'Professional Services',
    description: 'Consulting and professional services',
    unitPrice: 150,
    unit: 'hour',
    category: 'Services',
    sku: 'SRV-001'
  },
  {
    id: '2',
    name: 'Software License',
    description: 'Annual software license',
    unitPrice: 2500,
    unit: 'license',
    category: 'Software',
    sku: 'SW-001'
  },
  {
    id: '3',
    name: 'Training Program',
    description: 'Comprehensive training program',
    unitPrice: 800,
    unit: 'session',
    category: 'Training',
    sku: 'TRN-001'
  },
  {
    id: '4',
    name: 'Support Contract',
    description: '24/7 premium support contract',
    unitPrice: 1500,
    unit: 'month',
    category: 'Support',
    sku: 'SUP-001'
  }
];

const mockTemplates: Template[] = [
  {
    id: '1',
    name: 'Modern Professional',
    type: 'QUOTATION',
    htmlTemplate: '',
    isDefault: true
  },
  {
    id: '2',
    name: 'Classic Business',
    type: 'QUOTATION',
    htmlTemplate: '',
    isDefault: false
  },
  {
    id: '3',
    name: 'Minimal Clean',
    type: 'QUOTATION',
    htmlTemplate: '',
    isDefault: false
  }
];

const QuotationBuilder: React.FC<QuotationBuilderProps> = ({
  quotationId,
  onSave,
  onSend,
  onClose
}) => {
  const [quotation, setQuotation] = useState<Quotation>({
    quoteDate: new Date(),
    validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
    items: [],
    subtotal: 0,
    totalDiscount: 0,
    totalTax: 0,
    grandTotal: 0,
    notes: '',
    terms: 'Payment is due within 30 days of invoice date. Late payments may incur additional charges.',
    status: 'DRAFT',
    templateId: '1'
  });

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [templates] = useState<Template[]>(mockTemplates);
  const [selectedTemplate, setSelectedTemplate] = useState<Template>(templates[0]);
  
  const [isSaving, setIsSaving] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [editingItem, setEditingItem] = useState<QuotationItem | null>(null);
  const [contextIds, setContextIds] = useState<{ userId?: string; companyId?: string }>({});

  // Load customers from backend (public.customers)
  useEffect(() => {
    const load = async () => {
      try {
        let rows: any[] = [];
        // Prefer v2 with pagination
        try {
          const respV2: any = await apiService.getCustomersV2({ page: 1, limit: 100 });
          rows = Array.isArray(respV2?.data) ? respV2.data
            : Array.isArray(respV2?.customers) ? respV2.customers
            : (Array.isArray(respV2) ? respV2 : []);
        } catch (_) {}

        // Fallback to legacy endpoint if empty
        if (!rows || rows.length === 0) {
          try {
            const respLegacy: any = await apiService.getCustomers({ page: 1, limit: 100 });
            rows = Array.isArray(respLegacy?.data) ? respLegacy.data
              : Array.isArray(respLegacy?.customers) ? respLegacy.customers
              : (Array.isArray(respLegacy) ? respLegacy : []);
          } catch (_) {}
        }

        if (!rows || rows.length === 0) {
          message.info('No customers found. Please insert records into public.customers');
          rows = [];
        }

        const mapped: Customer[] = rows.map((c: any) => ({
          id: String(c.id || c.customerId || c.value || ''),
          name: c.name || c.customerName || '',
          email: c.email || '',
          phone: c.phone || c.contact || '',
          address: c.address || c.location || '',
          company: (typeof c.company === 'object' && c.company !== null)
            ? (c.company.name || c.company.code || '')
            : (c.company || undefined),
          companyId: String(c.companyId || c.company?.id || '') || undefined
        }));
        setCustomers(mapped);
      } catch (e) {
        message.error('Failed to load customers');
        setCustomers([]);
      }
    };
    load();
  }, []);

  // Load quotation by id for editing (backfill)
  useEffect(() => {
    const loadQuotation = async () => {
      if (!quotationId) return;
      try {
        const resp: any = await apiService.getQuotationByIdV2(quotationId);
        const q = resp?.quotation || resp || {};
        const items = (q.items || []).map((it: any) => ({
          id: String(it.id || Date.now() + Math.random()),
          productId: String(it.productId || ''),
          description: it.product?.itemName || it.productName || it.description || '',
          quantity: Number(it.quantity || 1),
          unitPrice: Number(it.unitPrice || 0),
          discount: Number(it.discount || 0),
          discountType: (it.discountType ? String(it.discountType).toLowerCase() : 'percentage'),
          taxRate: Number(it.taxRate || 0),
          total: Number(it.total || it.lineTotal || (Number(it.quantity || 0) * Number(it.unitPrice || 0)))
        }));

        // merge products to ensure select has options for existing items
        const minimalProducts: Product[] = (q.items || []).map((it: any) => ({
          id: String(it.productId || ''),
          name: it.product?.itemName || it.productName || '',
          description: it.product?.description || '',
          unitPrice: Number(it.unitPrice || 0),
          unit: it.product?.baseUOM || 'ea',
          category: it.product?.category || '',
          sku: it.product?.itemCode || it.productSku || ''
        }));
        setProducts(prev => {
          const map = new Map<string, Product>();
          [...minimalProducts, ...prev].forEach(p => { if (p.id) map.set(p.id, p); });
          return Array.from(map.values());
        });

        setQuotation(prev => ({
          ...prev,
          id: String(q.id || prev.id),
          // @ts-expect-error keep quoteNo if exists in downstream usage
          quoteNo: q.quotationNo || q.quoteNo || (prev as any).quoteNo,
          quoteDate: q.quoteDate ? new Date(q.quoteDate) : (q.createdAt ? new Date(q.createdAt) : prev.quoteDate),
          validUntil: q.validUntil ? new Date(q.validUntil) : prev.validUntil,
          customerId: q.customerId || prev.customerId,
          customer: q.customer ? { id: String(q.customer.id), name: q.customer.name, email: q.customer.email, address: q.customer.address } as any : prev.customer,
          items,
          notes: q.notes || prev.notes,
          terms: q.terms || prev.terms,
          status: q.status ? String(q.status).toUpperCase() as any : prev.status
        }));
      } catch (_) { /* ignore */ }
    };
    loadQuotation();
  }, [quotationId]);

  // Try to resolve current user/company for createQuotation required fields
  useEffect(() => {
    const initContext = async () => {
      try {
        const auth = localStorage.getItem('auth-storage');
        const parsed = (() => { try { return auth ? JSON.parse(auth) : null; } catch { return null; } })();
        const storedUserId = parsed?.state?.user?.id || localStorage.getItem('current-user-id') || localStorage.getItem('userId') || undefined;
        let storedCompanyId = parsed?.state?.user?.companyId || localStorage.getItem('current-company-id') || localStorage.getItem('companyId') || undefined;
        let resolvedUserId = storedUserId;
        let resolvedCompanyId = storedCompanyId;

        // Fallback: fetch first available user
        if (!resolvedUserId) {
          try {
            const usersResp: any = await apiService.getUsers({ page: 1, limit: 1 });
            const users = Array.isArray(usersResp?.data) ? usersResp.data : (Array.isArray(usersResp?.users) ? usersResp.users : (Array.isArray(usersResp) ? usersResp : []));
            if (users.length > 0 && users[0]?.id) resolvedUserId = String(users[0].id);
          } catch (_) {}
        }

        // Fallback: fetch first available company
        if (!resolvedCompanyId) {
          try {
            const companiesResp: any = await apiService.getCompanies({ page: 1, limit: 1 });
            const companies = Array.isArray(companiesResp?.data) ? companiesResp.data : (Array.isArray(companiesResp?.companies) ? companiesResp.companies : (Array.isArray(companiesResp) ? companiesResp : []));
            if (companies.length > 0 && companies[0]?.id) resolvedCompanyId = String(companies[0].id);
          } catch (_) {}
        }

        setContextIds({ userId: resolvedUserId, companyId: resolvedCompanyId });
        // 寫回 localStorage，避免下次再丟失
        try {
          if (resolvedUserId) localStorage.setItem('current-user-id', resolvedUserId);
          if (resolvedCompanyId) localStorage.setItem('current-company-id', resolvedCompanyId);
        } catch {}
      } catch (_) {
        setContextIds({});
      }
    };
    initContext();
  }, []);

  // Load products for selecting items (optional: keep mock if backend not ready)
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const auth = localStorage.getItem('auth-storage');
        const parsed = (() => { try { return auth ? JSON.parse(auth) : null; } catch { return null; } })();
        const companyId = parsed?.state?.user?.companyId || localStorage.getItem('current-company-id') || localStorage.getItem('companyId') || undefined;
        const resp: any = await apiService.getProductsV2({ page: 1, limit: 200, companyId: companyId || undefined });
        let rows: any[] = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp?.products) ? resp.products : (Array.isArray(resp) ? resp : []));

        // 如果帶 companyId 無資料，再嘗試不帶 companyId
        if ((!rows || rows.length === 0) && companyId) {
          const respNoCompany: any = await apiService.getProductsV2({ page: 1, limit: 200 });
          rows = Array.isArray(respNoCompany?.data) ? respNoCompany.data : (Array.isArray(respNoCompany?.products) ? respNoCompany.products : (Array.isArray(respNoCompany) ? respNoCompany : []));
        }

        // 最後退回 legacy
        if (!rows || rows.length === 0) {
          const legacy: any = await apiService.getProducts({ page: 1, limit: 200 });
          rows = Array.isArray(legacy?.data) ? legacy.data : (Array.isArray(legacy?.products) ? legacy.products : (Array.isArray(legacy) ? legacy : []));
        }

        const mapped: Product[] = (rows || []).map((p: any) => ({
          id: String(p.id || p.productId || ''),
          name: p.itemName || p.name || p.productName || '',
          description: p.description || '',
          unitPrice: Number(p.unitPrice || p.price || 0),
          unit: p.baseUOM || p.unit || 'ea',
          category: p.category || '',
          sku: p.itemCode || p.sku || p.productSku || '',
          alternateSkuList: p.alternateSkuList || [] // 備用 SKU
        }));
        setProducts(mapped);
        if ((!mapped || mapped.length === 0)) {
          message.info('No products found. Please insert records into public.products');
        }
      } catch (_) {}
    };
    loadProducts();
  }, []);

  // Calculate totals whenever items change
  useEffect(() => {
    const subtotal = quotation.items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
    const totalDiscount = quotation.items.reduce((sum, item) => {
      const discount = item.discountType === 'percentage' 
        ? (item.quantity * item.unitPrice * item.discount / 100)
        : item.discount;
      return sum + discount;
    }, 0);
    const totalTax = quotation.items.reduce((sum, item) => {
      const itemTotal = (item.quantity * item.unitPrice) - (
        item.discountType === 'percentage' 
          ? (item.quantity * item.unitPrice * item.discount / 100)
          : item.discount
      );
      return sum + (itemTotal * item.taxRate / 100);
    }, 0);

    const grandTotal = subtotal - totalDiscount + totalTax;

    setQuotation(prev => ({
      ...prev,
      subtotal,
      totalDiscount,
      totalTax,
      grandTotal,
      items: prev.items.map(item => {
        const itemSubtotal = item.quantity * item.unitPrice;
        const itemDiscount = item.discountType === 'percentage' 
          ? (itemSubtotal * item.discount / 100)
          : item.discount;
        const itemAfterDiscount = itemSubtotal - itemDiscount;
        const itemTax = itemAfterDiscount * item.taxRate / 100;
        const itemTotal = itemAfterDiscount + itemTax;
        
        return { ...item, total: itemTotal };
      })
    }));
  }, [quotation.items]);

  const selectCustomer = (customer: Customer) => {
    setQuotation(prev => ({
      ...prev,
      customerId: customer.id,
      customer
    }));
    setShowCustomerModal(false);
    // 自動記住公司上下文
    try {
      if (customer.companyId) {
        localStorage.setItem('current-company-id', customer.companyId);
      }
    } catch {}
    setContextIds(prev => ({ ...prev, companyId: customer.companyId || prev.companyId }));
  };

  const addItem = () => {
    const newItem: QuotationItem = {
      id: Date.now().toString(),
      productId: '',
      description: '',
      quantity: 1,
      unitPrice: 0,
      discount: 0,
      discountType: 'percentage',
      taxRate: 0,
      total: 0
    };
    setQuotation(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const updateItem = (itemId: string, updates: Partial<QuotationItem>) => {
    setQuotation(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.id === itemId ? { ...item, ...updates } : item
      )
    }));
  };

  const removeItem = (itemId: string) => {
    setQuotation(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };

  const saveQuotation = async () => {
    setIsSaving(true);
    try {
      // Build payload for backend
      if (!quotation.customerId || quotation.items.length === 0) {
        message.error('Please select a customer and add at least one item');
        setIsSaving(false);
        return;
      }

      // 盡力解析缺失的 user/company 上下文
      const auth = localStorage.getItem('auth-storage');
      const parsed = (() => { try { return auth ? JSON.parse(auth) : null; } catch { return null; } })();
      let resolvedUserId = contextIds.userId || parsed?.state?.user?.id || localStorage.getItem('current-user-id') || localStorage.getItem('userId') || undefined;
      let resolvedCompanyId = contextIds.companyId || parsed?.state?.user?.companyId || localStorage.getItem('current-company-id') || localStorage.getItem('companyId') || undefined;
      if (!resolvedUserId) {
        try {
          const usersResp: any = await apiService.getUsers({ page: 1, limit: 1 });
          const users = Array.isArray(usersResp?.data) ? usersResp.data : (Array.isArray(usersResp?.users) ? usersResp.users : (Array.isArray(usersResp) ? usersResp : []));
          if (users.length > 0 && users[0]?.id) resolvedUserId = String(users[0].id);
        } catch {}
      }
      if (!resolvedCompanyId) {
        try {
          // 優先取已選客戶的 companyId
          resolvedCompanyId = quotation.customer?.companyId || parsed?.state?.user?.companyId || localStorage.getItem('current-company-id') || undefined;
          if (!resolvedCompanyId) {
            const companiesResp: any = await apiService.getCompanies({ page: 1, limit: 1 });
            const companies = Array.isArray(companiesResp?.data) ? companiesResp.data : (Array.isArray(companiesResp?.companies) ? companiesResp.companies : (Array.isArray(companiesResp) ? companiesResp : []));
            if (companies.length > 0 && companies[0]?.id) resolvedCompanyId = String(companies[0].id);
          }
        } catch {}
      }
      if (resolvedUserId) localStorage.setItem('current-user-id', resolvedUserId);
      if (resolvedCompanyId) localStorage.setItem('current-company-id', resolvedCompanyId);
      setContextIds({ userId: resolvedUserId, companyId: resolvedCompanyId });
      if (!resolvedUserId || !resolvedCompanyId) {
        message.error('Missing user/company context. Please login or configure company.');
        setIsSaving(false);
        return;
      }

      const payload = {
        customerId: quotation.customerId,
        userId: resolvedUserId,
        companyId: resolvedCompanyId,
        validUntil: quotation.validUntil.toISOString(),
        notes: quotation.notes,
        terms: quotation.terms,
        items: quotation.items
          .filter(i => i.productId && i.quantity > 0)
          .map(i => ({ 
            productId: i.productId, 
            quantity: i.quantity, 
            unitPrice: i.unitPrice,
            discount: i.discount,
            discountType: i.discountType,
            taxRate: i.taxRate
          }))
      };

      const resp: any = await apiService.createQuotation(payload);

      const saved = resp?.data || resp;
      setQuotation(prev => ({
        ...prev,
        id: saved?.id || prev.id,
        quoteNo: saved?.quotationNo || saved?.quoteNo || prev.quoteNo,
        status: saved?.status ? String(saved.status).toUpperCase() as any : prev.status
      }));
      if (onSave) onSave(saved);
      message.success(`Quotation saved successfully${saved?.quotationNo ? ` (${saved.quotationNo})` : ''}`);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to save quotation';
      message.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const sendQuotation = async () => {
    if (!quotation.customerId || quotation.items.length === 0) {
      message.error('Please select a customer and add at least one item');
      return;
    }
    try {
      // 保存草稿（若尚未保存）
      if (!quotation.id) {
        await saveQuotation();
      }
      // 調用後端更新狀態
      if (quotation.id) {
        await apiService.updateQuotationStatusV2(quotation.id, 'SENT');
        setQuotation(prev => ({ ...prev, status: 'SENT' }));
        message.success('Quotation sent successfully');
        if (onSend) onSend({ ...quotation, status: 'SENT' });
      }
    } catch (error) {
      message.error('Failed to send quotation');
    }
  };

  const previewQuotation = () => {
    // This would normally open a preview modal or new window
    message.info('Preview functionality would open here');
  };

  const generateQuotationHTML = () => {
    return `
      <html>
        <head>
          <title>Quotation ${quotation.quoteNo || 'Draft'}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { border-bottom: 2px solid #333; padding-bottom: 20px; }
            .customer-info { margin: 20px 0; }
            .items-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            .items-table th, .items-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            .items-table th { background-color: #f2f2f2; }
            .totals { text-align: right; margin: 20px 0; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Quotation ${quotation.quoteNo || 'Draft'}</h1>
            <p>Date: ${quotation.quoteDate.toLocaleDateString()}</p>
            <p>Valid Until: ${quotation.validUntil.toLocaleDateString()}</p>
          </div>
          
          <div class="customer-info">
            <h3>Bill To:</h3>
            <p><strong>${quotation.customer?.name || 'Customer Name'}</strong></p>
            <p>${quotation.customer?.email || ''}</p>
            <p>${quotation.customer?.address || ''}</p>
          </div>
          
          <table class="items-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${quotation.items.map(item => `
                <tr>
                  <td>${item.description}</td>
                  <td>${item.quantity}</td>
                  <td>$${item.unitPrice.toFixed(2)}</td>
                  <td>$${item.total.toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="totals">
            <p>Subtotal: $${quotation.subtotal.toFixed(2)}</p>
            <p>Discount: -$${quotation.totalDiscount.toFixed(2)}</p>
            <p>Tax: $${quotation.totalTax.toFixed(2)}</p>
            <p><strong>Total: $${quotation.grandTotal.toFixed(2)}</strong></p>
          </div>
          
          ${quotation.notes ? `<div><h3>Notes:</h3><p>${quotation.notes}</p></div>` : ''}
          ${quotation.terms ? `<div><h3>Terms & Conditions:</h3><p>${quotation.terms}</p></div>` : ''}
        </body>
      </html>
    `;
  };

  const itemColumns: ColumnsType<QuotationItem> = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string, record: QuotationItem) => (
        <Space.Compact style={{ display: 'flex', width: '100%' }}>
          <Select
            showSearch
            placeholder="Select product"
            style={{ minWidth: 240 }}
            value={record.productId || undefined}
            onChange={(value, option: any) => {
              const prod: Product | undefined = option?.product;
              updateItem(record.id, {
                productId: value,
                description: prod?.name || record.description,
                unitPrice: prod ? prod.unitPrice : record.unitPrice,
                product: prod
              } as any);
            }}
            filterOption={(input, option: any) =>
              (option?.label || '').toLowerCase().includes(input.toLowerCase()) ||
              (option?.product?.sku || '').toLowerCase().includes(input.toLowerCase()) ||
              (option?.product?.alternateSkuList || []).some((sku: string) => sku.toLowerCase().includes(input.toLowerCase()))
            }
            options={products.map(p => ({ 
              key: p.id, 
              label: `${p.name} (${[p.sku, ...(p.alternateSkuList || [])].filter(Boolean).join(', ') || 'N/A'})`, 
              value: p.id, 
              product: p 
            }))}
          />
          <Input
            placeholder="Enter description"
            value={text}
            onChange={(e) => updateItem(record.id, { description: e.target.value })}
          />
        </Space.Compact>
      ),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (quantity: number, record: QuotationItem) => (
        <InputNumber
          min={1}
          value={quantity}
          onChange={(value) => updateItem(record.id, { quantity: value || 1 })}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 160,
      render: (price: number, record: QuotationItem) => (
        <InputNumber
          min={0}
          precision={2}
          value={price}
          onChange={(value) => updateItem(record.id, { unitPrice: value ?? 0 })}
          style={{ width: '100%' }}
          addonBefore="$"
          controls={false}
        />
      ),
    },
    {
      title: 'Discount',
      dataIndex: 'discount',
      key: 'discount',
      width: 120,
      render: (discount: number, record: QuotationItem) => (
        <Space.Compact style={{ display: 'flex' }}>
          <InputNumber
            min={0}
            max={record.discountType === 'percentage' ? 100 : undefined}
            precision={2}
            value={discount}
            onChange={(value) => updateItem(record.id, { discount: value || 0 })}
            style={{ flex: 1 }}
          />
          <Select
            value={record.discountType}
            onChange={(value) => updateItem(record.id, { discountType: value })}
            style={{ width: '60px' }}
          >
            <Option value="percentage">%</Option>
            <Option value="fixed">$</Option>
          </Select>
        </Space.Compact>
      ),
    },
    {
      title: 'Tax Rate',
      dataIndex: 'taxRate',
      key: 'taxRate',
      width: 140,
      render: (taxRate: number, record: QuotationItem) => (
        <InputNumber
          min={0}
          max={100}
          precision={2}
          value={taxRate}
          onChange={(value) => updateItem(record.id, { taxRate: value ?? 0 })}
          style={{ width: '100%' }}
          addonAfter="%"
          controls={false}
        />
      ),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 120,
      render: (total: number) => (
        <Text strong>${total.toFixed(2)}</Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record: QuotationItem) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            size="small"
            onClick={() => {
              setEditingItem(record);
              setShowProductModal(true);
            }}
          />
          <Popconfirm
            title="Delete this item?"
            onConfirm={() => removeItem(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              icon={<DeleteOutlined />}
              size="small"
              danger
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <Card style={{ marginBottom: '24px' }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={2} style={{ margin: 0 }}>
                <FileTextOutlined style={{ marginRight: '8px' }} />
                {quotationId ? '編輯報價單' : '新增報價單'}
              </Title>
              <Text type="secondary">
                {quotation.quoteNo || '草稿'} • <Tag color="blue">{quotation.status}</Tag>
              </Text>
            </Col>
            <Col>
              <Space>
                <Button
                  icon={<SettingOutlined />}
                  onClick={() => setShowTemplateSelector(true)}
                >
                  範本：{selectedTemplate.name}
                </Button>
                <Button
                  icon={<EyeOutlined />}
                  onClick={previewQuotation}
                >
                  預覽
                </Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={saveQuotation}
                  loading={isSaving}
                >
                  {isSaving ? '儲存中...' : '儲存'}
                </Button>
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={sendQuotation}
                  disabled={quotation.items.length === 0 || !quotation.customer}
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                >
                  發送
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        <Row gutter={[24, 24]}>
          {/* Left Column - Main Content */}
          <Col xs={24} lg={16}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              {/* Customer Information */}
              <Card title="客戶資訊">
                {!quotation.customer ? (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <UserOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                    <div>
                      <Text type="secondary">尚未選擇客戶</Text>
                    </div>
                    <div style={{ marginTop: '8px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        點擊「選擇客戶」以選擇客戶
                      </Text>
                    </div>
                    <Button
                      type="primary"
                      icon={<UserOutlined />}
                      onClick={() => setShowCustomerModal(true)}
                      style={{ marginTop: '16px' }}
                    >
                      選擇客戶
                    </Button>
                  </div>
                ) : (
                  <div>
                    <Row justify="space-between" align="top">
                      <Col>
                        <Space direction="vertical" size="small">
                          <Text strong style={{ fontSize: '16px' }}>
                            {quotation.customer.name}
                          </Text>
                          {quotation.customer.company && (
                            <Text type="secondary">{quotation.customer.company}</Text>
                          )}
                          <Space direction="vertical" size="small">
                            <Space>
                              <MailOutlined />
                              <Text>{quotation.customer.email}</Text>
                            </Space>
                            {quotation.customer.phone && (
                              <Space>
                                <PhoneOutlined />
                                <Text>{quotation.customer.phone}</Text>
                              </Space>
                            )}
                            {quotation.customer.address && (
                              <Space align="start">
                                <EnvironmentOutlined style={{ marginTop: '4px' }} />
                                <Text>{quotation.customer.address}</Text>
                              </Space>
                            )}
                          </Space>
                        </Space>
                      </Col>
                      <Col>
                        <Button
                          type="link"
                          onClick={() => setShowCustomerModal(true)}
                        >
                          變更
                        </Button>
                      </Col>
                    </Row>
                  </div>
                )}
              </Card>

              {/* Quote Details */}
              <Card title="報價資訊">
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item label="報價日期">
                      <DatePicker
                        style={{ width: '100%' }}
                        value={dayjs(quotation.quoteDate)}
                        onChange={(date) => setQuotation(prev => ({
                          ...prev,
                          quoteDate: date ? date.toDate() : new Date()
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="有效期限">
                      <DatePicker
                        style={{ width: '100%' }}
                        value={dayjs(quotation.validUntil)}
                        onChange={(date) => setQuotation(prev => ({
                          ...prev,
                          validUntil: date ? date.toDate() : new Date()
                        }))}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* Items */}
              <Card 
                title="項目" 
                extra={
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={addItem}
                  >
                    新增項目
                  </Button>
                }
              >
                {quotation.items.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <FileTextOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                    <div>
                      <Text type="secondary">尚未新增項目</Text>
                    </div>
                    <div style={{ marginTop: '8px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        點擊「新增項目」開始建立報價
                      </Text>
                    </div>
                  </div>
                ) : (
                  <Table
                    dataSource={quotation.items}
                    columns={itemColumns}
                    rowKey="id"
                    pagination={false}
                    scroll={{ x: true }}
                  />
                )}
              </Card>

              {/* Notes and Terms */}
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Card title="備註">
                    <TextArea
                      rows={4}
                      value={quotation.notes}
                      onChange={(e) => setQuotation(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="可填寫額外備註..."
                    />
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card title="條款與條件">
                    <TextArea
                      rows={4}
                      value={quotation.terms}
                      onChange={(e) => setQuotation(prev => ({ ...prev, terms: e.target.value }))}
                      placeholder="請輸入條款與條件..."
                    />
                  </Card>
                </Col>
              </Row>
            </Space>
          </Col>

          {/* Right Column - Sidebar */}
          <Col xs={24} lg={8}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              {/* Quote Summary */}
              <Card title="報價摘要">
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <Row justify="space-between">
                      <Text>小計：</Text>
                      <Text strong>${quotation.subtotal.toFixed(2)}</Text>
                    </Row>
                  </Col>
                  <Col span={24}>
                    <Row justify="space-between">
                      <Text>折扣總計：</Text>
                      <Text type="danger">-${quotation.totalDiscount.toFixed(2)}</Text>
                    </Row>
                  </Col>
                  <Col span={24}>
                    <Row justify="space-between">
                      <Text>稅額總計：</Text>
                      <Text>${quotation.totalTax.toFixed(2)}</Text>
                    </Row>
                  </Col>
                  <Col span={24}>
                    <Divider style={{ margin: '8px 0' }} />
                    <Row justify="space-between">
                      <Text strong style={{ fontSize: '16px' }}>總計：</Text>
                      <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                        ${quotation.grandTotal.toFixed(2)}
                      </Text>
                    </Row>
                  </Col>
                </Row>
              </Card>

              {/* Quick Actions */}
              <Card title="快速操作">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button
                    block
                    icon={<EyeOutlined />}
                    onClick={previewQuotation}
                  >
                    Preview
                  </Button>
                  <Button
                    block
                    icon={<PrinterOutlined />}
                    onClick={() => window.print()}
                  >
                    Print
                  </Button>
                  <Button
                    block
                    icon={<DownloadOutlined />}
                    onClick={() => {
                      const blob = new Blob([generateQuotationHTML()], { type: 'text/html' });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `quotation_${quotation.quoteNo || 'draft'}.html`;
                      link.click();
                      URL.revokeObjectURL(url);
                    }}
                  >
                    Download
                  </Button>
                  <Button
                    block
                    icon={<CopyOutlined />}
                    onClick={() => {
                      const duplicated = {
                        ...quotation,
                        id: undefined,
                        quoteNo: undefined,
                        status: 'DRAFT' as const,
                        quoteDate: new Date()
                      };
                      setQuotation(duplicated);
                      message.success('Quotation duplicated successfully');
                    }}
                  >
                    Duplicate
                  </Button>
                </Space>
              </Card>
            </Space>
          </Col>
        </Row>

        {/* Customer Selection Modal */}
        <Modal
          title="選擇客戶"
          open={showCustomerModal}
          onCancel={() => setShowCustomerModal(false)}
          footer={null}
          width={600}
          bodyStyle={{ maxHeight: 480, overflowY: 'auto', padding: 0 }}
        >
          <List
            style={{ maxHeight: 480, overflowY: 'auto', padding: 16 }}
            dataSource={customers}
            renderItem={(customer) => (
              <List.Item
                onClick={() => selectCustomer(customer)}
                style={{ cursor: 'pointer' }}
                actions={[
                  <Button type="link" onClick={() => selectCustomer(customer)}>
                    選擇
                  </Button>
                ]}
              >
                <List.Item.Meta
                  avatar={<Avatar icon={<UserOutlined />} />}
                  title={customer.name}
                  description={
                    <Space direction="vertical" size="small">
                      <Text>{customer.email}</Text>
                      {customer.phone && <Text type="secondary">{customer.phone}</Text>}
                      {customer.address && <Text type="secondary">{customer.address}</Text>}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        </Modal>

        {/* Template Selector Modal */}
        <Modal
          title="選擇範本"
          open={showTemplateSelector}
          onCancel={() => setShowTemplateSelector(false)}
          footer={null}
        >
          <List
            dataSource={templates}
            renderItem={(template) => (
              <List.Item
                onClick={() => {
                  setSelectedTemplate(template);
                  setQuotation(prev => ({ ...prev, templateId: template.id }));
                  setShowTemplateSelector(false);
                }}
                style={{ cursor: 'pointer' }}
                actions={[
                  <Button 
                    type={template.id === selectedTemplate.id ? 'primary' : 'default'}
                    onClick={() => {
                      setSelectedTemplate(template);
                      setQuotation(prev => ({ ...prev, templateId: template.id }));
                      setShowTemplateSelector(false);
                    }}
                  >
                    {template.id === selectedTemplate.id ? '已選取' : '選擇'}
                  </Button>
                ]}
              >
                <List.Item.Meta
                  title={template.name}
                  description={template.isDefault ? '預設範本' : '自訂範本'}
                />
              </List.Item>
            )}
          />
        </Modal>
      </div>
    </div>
  );
};

export default QuotationBuilder;